import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FollowedCharity } from '../../models/FollowedCharity';
import { Charity } from '../../models/Charity';
import { CharityWithTotals } from '../../models/CharityWithTotals';
import { Observable, throwError } from 'rxjs';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { User } from '../../models/User';
import { map, tap } from 'rxjs/operators';
import { ApiUrlService } from '../api/api-url.service';

interface CharityFollowResponse {
  isFollowing?: boolean;
  following?: boolean;
  id?: string;
  charityId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CharityFollowService {
  private readonly apiUrlService = inject(ApiUrlService);
  private get baseApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/followed-charities`); }

  private readonly _followingCount = signal<number>(0);
  readonly followingCount: Signal<number> = this._followingCount.asReadonly();
  readonly followingCount$ = toObservable(this._followingCount);

  constructor(private httpClient: HttpClientWrapperService) {}

  updateFollowingCount(count: number): void {
    this._followingCount.set(count);
  }

  getCurrentFollowingCount(): number {
    return this._followingCount();
  }

  isCharityFollowed(userId: string, charityId: string): Observable<boolean> {
    return this.httpClient.get<CharityFollowResponse>(
      `${this.baseApiUrl}?charityId=${charityId}&userId=${userId}`,
    ).pipe(
      map(response => {
        if (typeof response === 'boolean') {
          return response;
        }
        if (response && typeof response.isFollowing === 'boolean') {
          return response.isFollowing;
        }
        if (response && typeof response.following === 'boolean') {
          return response.following;
        }
        if (response && response.id) {
          return true;
        }
        if (response && Array.isArray(response)) {
          return response.some(charity =>
            charity.id === charityId ||
            charity.charityId === charityId ||
            charity.followedCharityId === charityId
          );
        }
        return false;
      })
    );
  }

  getFollowedCharitiesList(userId: string, includeTotals: boolean = false): Observable<Charity[] | CharityWithTotals[]> {
    const params: { includeTotals?: string | undefined } = includeTotals ? { includeTotals: 'true' } : {};
    return this.httpClient.get<Charity[] | CharityWithTotals[]>(`${this.baseApiUrl}/user/${userId}`, params).pipe(
      tap(charities => {
        this.updateFollowingCount(charities.length);
      })
    );
  }

  getFollowedUsersList(charityId: string): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.baseApiUrl}/charity/${charityId}`);
  }

  followCharity(userId: string, charity: Charity): Observable<FollowedCharity> {
    if (!userId || !charity?.id) {
      return throwError(() => new Error('userId and charity.id are required'));
    }

    const charityToFollow: FollowedCharity = {
      followingUserId: userId,
      followedCharityId: charity.id,
    };

    return this.httpClient.post<FollowedCharity, FollowedCharity>(
      `${this.baseApiUrl}`,
      charityToFollow,
    ).pipe(
      tap(() => {
        this._followingCount.set(this._followingCount() + 1);
      })
    );
  }

  unfollowCharity(userId: string, charityId: string): Observable<FollowedCharity> {
    const charityToUnfollow: FollowedCharity = {
      followingUserId: userId,
      followedCharityId: charityId,
    };

    return this.httpClient.post<FollowedCharity, FollowedCharity>(
      `${this.baseApiUrl}/unfollow`,
      charityToUnfollow,
    ).pipe(
      tap(() => {
        this._followingCount.set(Math.max(0, this._followingCount() - 1));
      })
    );
  }
}
